import React, {useState} from 'react';
import {Animated, StyleSheet, View} from 'react-native';

import theme from '../../style/theme';

import type {MenuAction} from './menu/menuTypes';
import type {MegaMenusResult} from './menu/useMegaMenus';
import {AccountMenuProps} from './AccountMenu';
import {useBand} from './breakpoints';
import {DesktopHeader} from './DesktopHeader';
import {HeaderDisabledEntry, isEntryDenied} from './headerDenyList';
import {heightsFor, StoreVariant} from './headerGeometry';
import {
  Availability,
  AVAILABILITY_COPY,
  GIFT_CARD_STORE_COPY,
  HeaderActions,
  HeaderState,
  NO_STORE_COPY,
} from './headerTypes';
import {MobileHeader} from './MobileHeader';
import {MobileMenuSheet} from './MobileMenuSheet';
import {hiddenWhen, useProgress} from './motion';
import {NonEcomNotice} from './NonEcomNotice';
import {useCompactHeader} from './useCompactHeader';

/** Store variant and compact flag to the header state (ruling 5; see HeaderState). */
export const headerStateFor = (variant: StoreVariant, compact: boolean): HeaderState =>
  variant === 'ecom' ? (compact ? 'collapsed' : 'default') : compact ? 'noEcomCollapsed' : 'noEcom';

export type SiteHeaderProps = {
  /**
   * `ecom`: menus, search and cart rows (ecommerce store, or no store selected). `nonEcom`:
   * the minimal rows (non-ecommerce store, gift-card store, blinker flows). Not "sells
   * online": BrandHeader also picks `nonEcom` for blinker flows (R37, R45).
   */
  variant: StoreVariant;
  /** Undefined: no store selected. */
  storeName?: string;
  availability: Availability;
  cartCount: number;
  /** Formatted, e.g. "$77.00". */
  cartTotal: string;
  showCart: boolean;
  /** False hides search, account and cart. */
  showUtilities: boolean;
  /** Renders NonEcomNotice under the rows; independent of the variant (R37). */
  showNonEcomNotice: boolean;
  /**
   * False hides the menu button on the mobile band (R38: BrandHeader passes `config.burger`).
   * The desktop band (>=1024) has none: user decision 2026-10-21 reverses R41.
   */
  showMenuButton: boolean;
  /**
   * DIS-2010: a selected in-store-only store (no online shopping). Mobile hides the menu
   * button and account (Figma 77:5858 variant 3). Not gift-card stores or blinker mode.
   */
  inStoreOnly: boolean;
  /**
   * Gift-card store (R42): the store selector is the plain text GIFT_CARD_STORE_COPY and the
   * logo is not pressable, because MY_STORE and HOME are not registered for gift-card stores.
   */
  giftCardStore: boolean;
  /** False removes the store selector press target (R37: blinker flows). */
  showStoreSelector: boolean;
  /** False hides the search control on both layouts (BrandHeader passes `config.search !== false`). */
  showSearch: boolean;
  denied: readonly HeaderDisabledEntry[];
  account: AccountMenuProps;
  /** Resolved mega menus from BrandHeader's `useMegaMenus` (C3). The desktop rows render them. */
  menus: MegaMenusResult;
  /** A live mega menu link was pressed (BrandHeader's `handleMenuAction`). */
  onMenuAction(a: MenuAction): void;
  actions: HeaderActions;
  /** Rendered under the header rows; moves up with the compact header (RP3). */
  routeRow?: React.ReactNode;
  /** Overrides useCompactHeader when defined (stories, tests). */
  compact?: boolean;
};

/**
 * DIS-1840 web header: picks the mobile or desktop-class rows by band, the rows by `variant`,
 * and full or compact by `useCompactHeader`. The deny-list `HEADER` check is BrandHeader's
 * (spec § 7.1), not this component's.
 *
 * No element here paints a ground. Each row paints its own, so a hidden row leaves a
 * transparent band through which the page shows and receives presses (R40).
 */
export const SiteHeader = ({
  variant,
  storeName,
  availability,
  cartCount,
  cartTotal,
  showCart,
  showUtilities,
  showNonEcomNotice,
  showMenuButton,
  inStoreOnly,
  giftCardStore,
  showStoreSelector,
  showSearch,
  denied,
  account,
  actions,
  menus,
  onMenuAction,
  routeRow,
  compact: compactOverride,
}: SiteHeaderProps) => {
  const band = useBand();
  // DIS-2010 (Figma 77:5858): an ecommerce mobile header opens full (strap + nav + search,
  // 152) and compacts on scroll to strap + nav (92). Mobile with search off mounts no search
  // row (MobileHeader), so the header is 92 full and compact, as the nonEcom geometry:
  // compact moves nothing (offset and route row stay put).
  const geometry = band === 'mobile' && !(showUtilities && showSearch) ? 'nonEcom' : variant;
  // Called unconditionally (rules of hooks); the prop wins when defined.
  const compactFromScroll = useCompactHeader(band, geometry);
  const compact = compactOverride ?? compactFromScroll;
  const state = headerStateFor(variant, compact);
  const progress = useProgress(compact);
  const {full, compact: compactHeight} = heightsFor(band, geometry);
  // R60: the menu sheet replaces the drawer on web; the mobile menu button opens it. The
  // desktop-class header has no menu button (2026-10-21 reverses R41).
  const [sheetOpen, setSheetOpen] = useState(false);
  const openSheet = () => setSheetOpen(true);
  const closeSheet = () => setSheetOpen(false);
  // As the desktop nav row: no menus for a non-ecommerce store or a failed load (spec § 7.3.2),
  // and `denied` applies, so the deny-list stories drive the sheet too.
  const sheetMenus =
    variant === 'ecom' && !menus.error
      ? menus.data.filter(menu => !isEntryDenied(menu.flag, denied))
      : [];

  return (
    <View testID="site-header" pointerEvents="box-none">
      {band === 'mobile' ? (
        <MobileHeader
          state={state}
          storeName={storeName}
          availability={availability}
          cartCount={cartCount}
          showCart={showCart}
          showUtilities={showUtilities}
          showMenuButton={showMenuButton}
          inStoreOnly={inStoreOnly}
          giftCardStore={giftCardStore}
          showStoreSelector={showStoreSelector}
          showSearch={showSearch}
          actions={actions}
          account={account}
          onMenuPress={openSheet}
        />
      ) : (
        <DesktopHeader
          state={state}
          storeName={storeName}
          availability={availability}
          cartCount={cartCount}
          cartTotal={cartTotal}
          showCart={showCart}
          showUtilities={showUtilities}
          giftCardStore={giftCardStore}
          showStoreSelector={showStoreSelector}
          showSearch={showSearch}
          denied={denied}
          account={account}
          actions={actions}
          menus={menus}
          onMenuAction={onMenuAction}
        />
      )}
      {sheetOpen ? (
        <MobileMenuSheet
          menus={sheetMenus}
          loading={menus.isLoading}
          storeName={giftCardStore ? GIFT_CARD_STORE_COPY : (storeName ?? NO_STORE_COPY)}
          availability={
            storeName && !giftCardStore
              ? AVAILABILITY_COPY[variant === 'ecom' ? availability : 'inStore']
              : undefined
          }
          onStorePress={giftCardStore || !showStoreSelector ? undefined : actions.onStorePress}
          onAction={onMenuAction}
          onClose={closeSheet}
        />
      ) : null}
      {showNonEcomNotice ? (
        // R40 (supersedes R30): the slot paints the page ground and fades and slides up with
        // the notice, so compact exposes no ground of its own. RP6: it slides behind the
        // header rows (their roots sit at zIndex 10).
        <Animated.View
          testID="site-header-non-ecom-notice-slot"
          style={[
            styles.noticeGround,
            {
              opacity: progress.interpolate({inputRange: [0, 1], outputRange: [1, 0]}),
              transform: [
                {
                  translateY: progress.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0, -compactHeight],
                  }),
                },
              ],
            },
          ]}
          {...hiddenWhen(compact)}>
          <NonEcomNotice band={band} onSelectStore={actions.onStorePress} />
        </Animated.View>
      ) : null}
      {routeRow ? (
        // RP3: compact moves the route row up to the compact header's bottom edge.
        <Animated.View
          testID="site-header-route-row"
          pointerEvents="box-none"
          style={{
            transform: [
              {
                translateY: progress.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0, compactHeight - full],
                }),
              },
            ],
          }}>
          {routeRow}
        </Animated.View>
      ) : null}
    </View>
  );
};

export default SiteHeader;

const styles = StyleSheet.create({
  noticeGround: {backgroundColor: theme.colour.background.general.screen},
});
